import React, { useEffect, useRef } from "react";

const InfiniteScrollComponent = ({
  next,
  hasNext,
  children,
  className,
  style,
  skeleton = null,
  isLoading = false,
  rootRef = null,
}) => {
  const observerRef = useRef(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    // While a page is loading, do not observe — prevents open→loop storms
    if (isLoading) {
      inFlightRef.current = true;
      return undefined;
    }

    inFlightRef.current = false;

    const observerTarget = observerRef.current;
    if (!observerTarget || !hasNext) return undefined;

    const root = rootRef?.current ?? null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || inFlightRef.current || !hasNext) return;

        inFlightRef.current = true;
        Promise.resolve(next()).catch(() => {
          inFlightRef.current = false;
        });
      },
      {
        root,
        // Small margin only — large rootMargin made the sentinel "visible" immediately
        rootMargin: "40px",
        threshold: 0,
      }
    );

    observer.observe(observerTarget);

    return () => {
      observer.disconnect();
    };
  }, [next, hasNext, isLoading, rootRef]);

  return (
    <div
      className={className}
      style={{
        width: "100%",
        ...style,
      }}
    >
      {children}

      {isLoading ? skeleton : null}

      {hasNext ? (
        <div
          ref={observerRef}
          aria-hidden="true"
          style={{ width: "100%", height: 1, flexShrink: 0 }}
        />
      ) : null}
    </div>
  );
};

export default InfiniteScrollComponent;
